import { useChatEvent } from '@/hooks/useChatEvents';
import useChatStore from '@/store/useChatStore';
import { GroupChat } from '@/types/chat';
import {
  Action as AlertDialogAction,
  Cancel as AlertDialogCancel,
  Content as AlertDialogContent,
  Description as AlertDialogDescription,
  Overlay as AlertDialogOverlay,
  Portal as AlertDialogPortal,
  Root as AlertDialogRoot,
  Title as AlertDialogTitle,
  Trigger as AlertDialogTrigger
} from '@radix-ui/react-alert-dialog';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Avatar from '../common/Avatar';
import GroupUserList from './GroupUserList';

const GroupChatHeader: React.FC = () => {
  const { t } = useTranslation();
  const currentChat = useChatStore((state) => state.currentChat);
  const copyShareLink = useChatStore((state) => state.copyShareLink);
  const leaveCurrentChat = useChatStore((state) => state.leaveCurrentChat);
  const isConnecting = useChatStore((state) => state.isConnecting);

  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [isUserListOpen, setIsUserListOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const checkScreenSize = () => { setIsMobile(window.innerWidth < 768); };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => {
      window.removeEventListener('resize', checkScreenSize);
      if (copyTimeoutRef.current) { clearTimeout(copyTimeoutRef.current); }
    };
  }, []);

  useChatEvent('linkCopied', () => {
    if (copyTimeoutRef.current) { clearTimeout(copyTimeoutRef.current); copyTimeoutRef.current = null; }
    setIsCopying(false);
    toast.success(t('toast.linkCopied'));
  });

  if (!currentChat || !currentChat.isGroup) { return null; }

  const groupChat = currentChat as GroupChat;

  const handleCopyLink = () => {
    if (!groupChat.shareLink) { toast.error(t('toast.cannotGetLink')); return; }
    setIsCopying(true);
    try {
      copyShareLink?.();
      const timeoutId = setTimeout(() => { setIsCopying(false); }, 3000);
      return () => clearTimeout(timeoutId);
    } catch { setIsCopying(false); }
  };

  const handleLeaveChat = () => { leaveCurrentChat?.(); setLeaveDialogOpen(false); };

  return (
    <>
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <Avatar alt={groupChat.name} size="md" isHost={groupChat.isHost} />
          <div className="ml-3">
            <h2 className="text-sm font-medium text-gray-900">{groupChat.name}</h2>
            <p className="text-xs text-gray-400">
              {t('group.memberCount', { count: groupChat.users.length })}
              {isConnecting && (
                <span className="ml-2 inline-flex items-center text-blue-600">
                  <svg className="w-3 h-3 mr-1 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  {t('group.connecting')}
                </span>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-1">
          {isMobile && (
            <button className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors md:hidden" onClick={() => setIsUserListOpen(true)} title={t('group.viewMembers')}>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </button>
          )}

          <button className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" disabled={isConnecting || isCopying} onClick={handleCopyLink} title={t('group.copyInviteLink')}>
            {isCopying ? (
              <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
            )}
          </button>

          <AlertDialogRoot open={leaveDialogOpen} onOpenChange={setLeaveDialogOpen}>
            <AlertDialogTrigger asChild>
              <button className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" disabled={isConnecting}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              </button>
            </AlertDialogTrigger>
            <AlertDialogPortal>
              <AlertDialogOverlay className="fixed inset-0 bg-black/30 animate-[overlay-show_150ms_cubic-bezier(0.16,1,0.3,1)]" />
              <AlertDialogContent className="fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[90vw] max-w-[400px] rounded-xl bg-white shadow-xl border border-gray-200 p-6 focus:outline-none animate-[content-show_150ms_cubic-bezier(0.16,1,0.3,1)]">
                <AlertDialogTitle className="text-lg font-semibold text-gray-900">{t('group.leaveGroup')}</AlertDialogTitle>
                <AlertDialogDescription className="text-gray-500 mt-2 mb-6 text-sm">{t('group.leaveGroupDesc')}</AlertDialogDescription>
                <div className="flex justify-end space-x-2">
                  <AlertDialogCancel className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">{t('group.cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={handleLeaveChat} className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium">{t('group.leave')}</AlertDialogAction>
                </div>
              </AlertDialogContent>
            </AlertDialogPortal>
          </AlertDialogRoot>
        </div>
      </div>

      {isMobile && isUserListOpen && (
        <>
          <div className="fixed inset-0 bg-black/20 z-40 md:hidden" onClick={() => setIsUserListOpen(false)} />
          <div className="fixed inset-y-0 right-0 z-50 w-72 bg-white shadow-xl border-l border-gray-200 transform transition-transform duration-300 md:hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900">{t('group.groupMembers')}</h3>
              <button onClick={() => setIsUserListOpen(false)} className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto"><GroupUserList /></div>
          </div>
        </>
      )}
    </>
  );
};

export default GroupChatHeader;
